/**
 * Store Utility.
 *
 * Abstracts the persistence hierarchy:
 * sessionStorage => cookieStorage => getter() function.
 *
 */
// imports
import CookieStore from './storecookie';
import SessionStore from './storesession';

/**
 * Store Utility class.
 */
export default class Store {
    
    // attribute setup
    public session: SessionStore;
    public cookie: CookieStore;
    
    /**
     * Store constructor.
     *
     * Set the child store objects for each storage mechanism supported
     * in the hierarchy.
     */
    constructor() {
        this.session = new SessionStore();
        this.cookie = new CookieStore();
    }

    /**
     * Passively get the client storage object located at key.
     * Handles if client storage is not available by returning null.
     *
     * @param {string} key
     * @return {StoreGetResult}
     */
    public get(key: string): Array<string> {
        let value: Array<string> = [];
        if (this.session.status === true) {
            value = this.session.get(key);
        }
        else if (this.cookie.status === true) {
            value = this.cookie.get(key);
        }
        // return void on double-failure
        // no client-side persistence was available
        return value;
    }

    /**
     * Passively set the client storage object located at key with value.
     * Handles if client storage is not available by returning false.
     *
     * @param {string} key
     * @param {string} value
     * @return {StoreSetResult}
     */
    set(key: string, value: Array<string>): boolean {
        if (this.session.status === true) {
            return this.session.set(key, value);
        }
        if (this.cookie.status === true) {
            return this.cookie.set(key, value);
        }
        // return false on double-failure
        // as a sign of a bad (error) result
        return false;
    }

    /**
     * Passively remove the client storage object located at key.
     * Removes from every available backing store so no stale copy is left
     * behind in the hierarchy.
     *
     * @param {string} key
     */
    remove(key: string): void {
        if (this.session.status === true) {
            this.session.remove(key);
        }
        if (this.cookie.status === true) {
            this.cookie.remove(key);
        }
    }

}
